<template>
    <Btn
        :loading="loading"
        @click="onVoteStart"
        @mouseleave="onVoteEnd"
    >
        <div
            v-show="!rateFormOpened"
            class="flex items-center justify-center whitespace-nowrap"
        >
            <i
                :class="[
                    'icon',
                    userVote ? 'icon-star-full text-yellow-300' : 'icon-star',
                    $attrs.disabled ? '' : 'text-black',
                ]"
            ></i>
            <div class="font-body-3-regular">
                <span class="pl-1">
                    {{ avgScore.toFixed(1) }}
                </span>
                <span :class="$attrs.disabled ? '' : 'text-slate-400'">
                    ({{ convertToReadableNumber(ratingsCount) }})
                </span>
            </div>
        </div>

        <div
            v-show="rateFormOpened"
            class="flex items-center justify-center"
        >
            <i
                v-for="index in 5"
                :key="index"
                :class="[
                    userVote >= index ? 'icon-star-full text-yellow-300' : 'icon-star text-slate-600',
                    { 'mr-1': index < 5 },
                    'icon',
                ]"
                @click.stop="doVote(index)"
            ></i>
        </div>
    </Btn>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, defineAsyncComponent } from 'vue';
import type { EnumToUnion } from '@/types/misc';
import type { ModalFormRateType } from '@/components/ModalFormRate';
import { convertToReadableNumber } from '@/helpers/string';

/**
 * This is the rate button that we have currently for rating documents and flashcards.
 * It relays on the basic Btn component.
 * When you click on it in the default state it will open to show five icons,
 * then click on one of them to trigger a rate-event with the value provided from 1 to 5
 *
 */
export default defineComponent({
    name: 'BtnRate',
    props: {
        /**
         * current vote of the auth user
         */
        userVote: {
            type: Number,
            default: 0,
        },
        /**
         * current avg score of the object
         */
        avgScore: {
            type: Number,
            default: 0,
        },
        /**
         * current number of ratings of the object
         */
        ratingsCount: {
            type: Number,
            default: 0,
        },
        /**
         * defines the type of content to rate.
         */
        contentType: {
            type: String as PropType<EnumToUnion<ModalFormRateType>>,
            required: true,
        },
        /**
         * Function that is executed after a vote change.
         * It can include a `reason` or `comment` if the vote was low.
         * If the on vote function was successful, it should return true.
         */
        onVote: {
            type: Function as PropType<
                (
                    vote: number,
                    reasonId?: number,
                    comment?: string,
                ) => Promise<undefined | { avgScore: number; ratingsCount: number }>
            >,
            required: true,
        },
    },
    emits: [
        'update:user-vote',
        'update:avg-score',
        'update:ratings-count',
    ],
    data() {
        return {
            loading: false,
            rateFormOpened: false,
        };
    },
    computed: {
        userUpvoted() {
            return this.userVote >= 3;
        },
    },
    methods: {
        onVoteStart() {
            if (!this.$attrs.disabled) {
                this.rateFormOpened = true;
            }
        },
        async doVote(value: number) {
            if (this.loading || value === this.userVote) return;
            this.loading = true;

            const handleVote = async (reason?: number, comment?: string) => {
                const response = await this.onVote(value, reason, comment);

                if (response) {
                    this.$emit('update:user-vote', value);
                    this.$emit('update:avg-score', response.avgScore);
                    this.$emit('update:ratings-count', response.ratingsCount);
                }

                return response;
            };

            try {
                if (value > 2) {
                    await handleVote();
                } else {
                    await this.$modal.open(
                        defineAsyncComponent(() => import('@/components/ModalFormRate')),
                        {
                            type: this.contentType,
                            submitAction: ({ reason, comment }: { reason: number; comment: string }) =>
                                handleVote(reason, comment),
                        },
                    );
                }
            } finally {
                this.onVoteEnd();
            }
        },
        onVoteEnd() {
            this.loading = false;
            this.rateFormOpened = false;
        },
        convertToReadableNumber,
    },
});
</script>
